import { asRecordingId } from "@repo/app-types/entities/recording";
import type { AccountGradingRecording } from "@repo/app-types/entities/teammatePerformanceSummary";
import isEqual from "lodash-es/isEqual.js";
import { useCallback, useEffect, useState, type RefObject } from "react";
import { computeIdealScrollTopForElement } from "../../components/transcript/scroll.js";
import type { CallPosition } from "./teammateAccountCallOutlineNavigation.js";

interface UseTranscriptOutlineSyncOptions {
  transcriptContainerRef: RefObject<HTMLElement | null>;
  sortedRecordings: readonly AccountGradingRecording[];
}

export function useTranscriptOutlineSync(
  options: UseTranscriptOutlineSyncOptions,
) {
  const { transcriptContainerRef, sortedRecordings } = options;

  const [callPosition, setCallPosition] = useState<CallPosition | undefined>(
    undefined,
  );

  // Scrolls the transcript to the given call position.
  const scrollToCallPosition = useCallback(
    (position: CallPosition): void => {
      const transcriptContainer = transcriptContainerRef.current;
      if (!transcriptContainer) return;

      // Find the recording and fragment that matches the requested position
      // TODO: We should probably find the closest edge, not the first one that intersects.
      const recording = sortedRecordings.find(
        (rec) => rec.id === position.recordingId,
      );
      const fragment = recording?.transcript?.fragments.find(
        (frag) => position.timestamp <= frag.end_time,
      );
      if (!fragment) return;

      const element = transcriptContainer.querySelector(
        `[data-recording-id="${position.recordingId}"][data-timestamp="${fragment.start_time}"]`,
      );
      if (!element) return;

      transcriptContainer.scrollTo({
        top: computeIdealScrollTopForElement(transcriptContainer, element),
        behavior: "smooth",
      });
    },
    [sortedRecordings, transcriptContainerRef],
  );

  // Updates the current position shown in the outline when recordings load or when the transcript is scrolled
  const recordingCount = sortedRecordings.length;
  useEffect(() => {
    const transcriptContainer = transcriptContainerRef.current;
    if (!transcriptContainer) return;

    const handleScroll = () => {
      // Look for the element with an ideal scroll position closest to the current scroll position.
      // TODO: We should probably look for the closest edge, not the middle of the element.
      const currentScrollTop = transcriptContainer.scrollTop;
      let closestElement: Element | undefined = undefined;
      let closestDistance = Infinity;

      // TODO: Optimize this, such as by doing a binary search.
      for (const element of transcriptContainer.querySelectorAll(
        "[data-recording-id][data-timestamp]",
      )) {
        const idealScrollTop = computeIdealScrollTopForElement(
          transcriptContainer,
          element,
        );
        const distance = Math.abs(idealScrollTop - currentScrollTop);

        // We should gradually approach the closest element
        if (distance < closestDistance) {
          closestElement = element;
          closestDistance = distance;
          continue;
        }

        // If we're getting further away, we can stop.
        if (isFinite(closestDistance) && distance > closestDistance) {
          break;
        }
      }

      if (!closestElement) return;

      const recordingId = closestElement.getAttribute("data-recording-id");
      const tsAttr = closestElement.getAttribute("data-timestamp");
      if (!recordingId || !tsAttr) return;

      const timestamp = Number(tsAttr);
      const nextPosition: CallPosition = {
        recordingId: asRecordingId(recordingId),
        timestamp,
      };

      // Make sure we only update the position if it has changed to avoid unnecessary re-renders
      setCallPosition((prevPosition) =>
        isEqual(prevPosition, nextPosition) ? prevPosition : nextPosition,
      );
    };

    transcriptContainer.addEventListener("scroll", handleScroll);
    handleScroll();

    return () =>
      transcriptContainer.removeEventListener("scroll", handleScroll);
  }, [recordingCount, transcriptContainerRef]);

  return { callPosition, scrollToCallPosition } as const;
}
